import type { SelectChangeEvent } from "@mui/material";
import { Box, MenuItem, Select } from "@mui/material";
import { useMemo } from "react";
import type {
  MultiSelectObjectFilter,
  MultiSelectObjectFilterOperator,
  SelectOption,
} from "../../api/types/objectTypes";
import { allMultiSelectObjectFilterOperators } from "../../api/types/objectTypes";
import MultiselectAutocomplete from "../common/MultiselectAutocomplete";
import { multiSelectFilterOperatorCaptions } from "./objectFilterHelpers";

interface Props {
  filter: MultiSelectObjectFilter;
  onFilterChange: (filter: MultiSelectObjectFilter) => void;
  selectOptions?: SelectOption[];
}

const noSelectOptions: SelectOption[] = [];

const ObjectMultiSelectFilterEditorContent = ({ filter, onFilterChange, selectOptions }: Props) => {
  const allOptions = selectOptions ?? noSelectOptions;
  const selectedValues = filter.selectedValues;

  // MUI's Autocomplete clears the typed text whenever `value` changes identity, so keep it referentially stable.
  const values = useMemo(
    () => allOptions.filter((o) => selectedValues?.includes(o.value)),
    [allOptions, selectedValues]
  );

  const handleFilterOperatorChange = (event: SelectChangeEvent<string>) => {
    const newOperator = event.target.value as MultiSelectObjectFilterOperator;
    onFilterChange({
      operator: newOperator,
      selectedValues: newOperator === "OneOf" || newOperator === "NotOneOf" ? filter.selectedValues : undefined,
    });
  };

  const showValues = filter.operator === "OneOf" || filter.operator === "NotOneOf";

  return (
    <>
      <Select value={filter.operator} onChange={handleFilterOperatorChange} sx={{ flex: "0 0 12.5rem" }}>
        {allMultiSelectObjectFilterOperators.map((operator) => (
          <MenuItem key={operator} value={operator}>
            {multiSelectFilterOperatorCaptions[operator]}
          </MenuItem>
        ))}
      </Select>

      {showValues && (
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <MultiselectAutocomplete
            dense
            options={allOptions}
            values={values}
            onSelected={(items) => onFilterChange({ ...filter, selectedValues: items.map((o) => o.value) })}
            getOptionLabel={(o) => o.label}
            getOptionValue={(o) => o.value}
            renderValue={(items) => items.map((o) => o.label).join(", ")}
          />
        </Box>
      )}
    </>
  );
};

export default ObjectMultiSelectFilterEditorContent;
